import {
  DateTimePicker,
  Modal,
  PrimaryButton,
  setDateWithTimezone,
  toISOStringWithoutTimezone,
  toast,
  Hint,
  getFormattedUtcOffset,
} from '@systemeio/ui'
import { useScheduleNewsletter } from 'modules/newsletters/single/hooks/use-schedule-newsletter'
import { NewsletterId } from 'modules/newsletters/single/types/newsletter-interface'
import {
  NewsletterViewApiInterface,
  NewsletterViewApiType,
} from 'modules/newsletters/single/types/newsletter-view-interface'
import React, { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { filterPassedTime, removeTimezoneFromString } from 'shared/utils/date-time-format'
import { KeyedMutator } from 'swr'
import TransLoco from 'shared/components/trans-loco'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'

export interface ScheduleNewsletterModalProps {
  scheduledAt: NewsletterViewApiInterface['scheduledAt'] | undefined
  newsletterId: NewsletterId
  onSave: () => Promise<void>
  onClose: () => void
  opened: boolean
  isTagsSelected: boolean
  mutateNewslettersData: KeyedMutator<NewsletterViewApiType>
  title: string
}

export const ScheduleNewsletterModal = ({
  scheduledAt,
  newsletterId,
  onSave,
  onClose,
  opened,
  isTagsSelected,
  mutateNewslettersData,
  title,
}: ScheduleNewsletterModalProps) => {
  const { t } = useLocoTranslation()

  const { user, intlLocale } = useUser()

  const [date, setDate] = useState<string | null>(null)
  const [error, setError] = useState<string>('')
  const [isScheduleNewsletterFetching, setIsScheduleNewsletterFetching] = useState<boolean>(false)

  const { fetcherScheduleNewsletter } = useScheduleNewsletter()

  const maxDate = setDateWithTimezone(undefined, user?.timezone).setFullYear(
    setDateWithTimezone(undefined, user?.timezone).getFullYear() + 1,
  )

  const handelClose = () => {
    onClose()
    setDate(null)
    setError('')
  }

  useEffect(() => {
    if (opened && !date) {
      if (scheduledAt) {
        const data = removeTimezoneFromString(scheduledAt)
        setDate(data)
      } else {
        const date = setDateWithTimezone(undefined, user?.timezone)
        date.setMinutes(date.getMinutes() + 1)
        setDate(toISOStringWithoutTimezone(date))
      }
    }
  }, [scheduledAt, date, opened, user?.timezone])

  const handleConfirm = async () => {
    if (!date) return
    if (!isTagsSelected) {
      return toast.error(t('dashboard.newsletter_editor.select_tags_warning_message'))
    }
    setIsScheduleNewsletterFetching(true)
    try {
      await onSave()
      await fetcherScheduleNewsletter(newsletterId, { sendAt: date })
      await mutateNewslettersData(data => data && { ...data, scheduledAt: date })
      handelClose()
    } catch (e) {}
    setIsScheduleNewsletterFetching(false)
  }

  return (
    <>
      <Modal
        isFetching={isScheduleNewsletterFetching}
        isCentered
        opened={opened}
        onClose={onClose}
        title={title}
      >
        <form className="m-auto flex max-w-fit flex-col gap-6 lg:gap-10">
          <div className={'flex flex-col gap-2'}>
            <DateTimePicker
              customProps={{
                minDate: setDateWithTimezone('', user?.timezone),
                maxDate: new Date(maxDate),
                filterTime: time => filterPassedTime(time, user?.timezone),
              }}
              key={'date'}
              value={date}
              onChange={date => {
                setDate(date)
              }}
              error={error}
              createdAt={user?.createdAt}
              timezone={user?.timezone}
              userDateFormat={user?.dateFormat}
              userTimeFormat={user?.timeFormat}
              intlLocale={intlLocale}
            />
            <Hint
              label={
                <TransLoco
                  t={t}
                  i18nKey={'dashboard.newsletter.schedule_modal_hint_tooltip'}
                  components={{
                    a: (
                      <LinkWithoutPrefetch
                        href={'/profile/account-settings'}
                        target="_blank"
                        rel="noreferrer"
                        className={'primary-link'}
                      />
                    ),
                  }}
                />
              }
            >
              <TransLoco
                t={t}
                i18nKey="dashboard.newsletter.schedule_modal_hint"
                values={{
                  timezone: `${user?.timezone}`,
                }}
              />
              {` (${getFormattedUtcOffset(user?.dashboardLocale, user?.timezone)})`}
            </Hint>
          </div>
          <div className="flex justify-center">
            <PrimaryButton
              isFetching={isScheduleNewsletterFetching}
              disabled={!date}
              width="large"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                handleConfirm()
              }}
              type="submit"
            >
              {t('dashboard.newsletter_editor.save_and_schedule')}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </>
  )
}
